import { useCallback, useState } from "react";
import { renderCMSComponent } from "~/features/cms/componentFactory";
import type { CMSPhotoGalleryDTO } from "~/features/cms/types/dto";
import { IconButton, styled } from "~/common/mui";
import { SIZE_DESKTOP_S, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { makeExternallyStyledBaseElement } from "~/features/cms/utils/styles";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

interface Props extends Omit<CMSPhotoGalleryDTO, "type"> {
  className?: string;
}

export const CMSPhotoGallery = (props: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const { images } = props;

  const size = useNumericScreenSize();
  const isDesktop = size >= SIZE_DESKTOP_S;

  const numberOfItemsInView = isDesktop ? 4 : 3;

  const isNextDisabled = () => {
    if (images.length == 3) {
      return activeIndex == 2;
    }
    return activeIndex > images.length - numberOfItemsInView;
  };

  const isPrevDisabled = () => {
    return activeIndex <= 0;
  };

  const nextSlide = () => {
    if (images.length == 3) {
      setActiveIndex((prev) => prev + 2);
    } else if (isNextDisabled()) {
      return;
    } else if (activeIndex == images.length - numberOfItemsInView) {
      setActiveIndex((prev) => prev + 2);
    } else {
      setActiveIndex((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (images.length == 3) {
      setActiveIndex((prev) => prev - 2);
    } else if (isPrevDisabled()) {
      return;
    } else if (activeIndex == images.length - numberOfItemsInView + 2) {
      setActiveIndex((prev) => prev - 2);
    } else {
      setActiveIndex((prev) => prev - 1);
    }
  };

  const getWidth = useCallback(
    (index: number) => {
      const remainingItems = images.length - activeIndex;
      if (images.length < 3) {
        return "100%";
      } else if (images.length == 3) {
        const specialWidths = ["100%", "100%", "56px"];
        return activeIndex === 0 ? specialWidths[index] : specialWidths.reverse()[index];
      } else if (remainingItems < numberOfItemsInView) {
        if (index === images.length - 1) {
          return "100%";
        }
        if (index === images.length - 2) {
          return "100%";
        }
        if (index === images.length - 3) {
          return isDesktop ? "120px" : "56px";
        }
        if (index === images.length - 4 && isDesktop) {
          return "56px";
        }
        return "0%";
      } else {
        if (index === 0) {
          return "100%";
        }
        if (index === 1) {
          return "100%";
        }
        if (index === 2) {
          return isDesktop ? "120px" : "56px";
        }
        if (index === 3 && isDesktop) {
          return "56px";
        }
      }
      return "0%";
    },
    [activeIndex, images.length, numberOfItemsInView, size],
  );

  const getMargin = useCallback(
    (relativeIndex: number, index: number) => {
      const remainingItems = images.length - activeIndex + 1;
      if (remainingItems <= numberOfItemsInView) {
        return index > images.length - numberOfItemsInView - 1 && index != images.length - 1 ? "8px" : "0px";
      } else {
        return relativeIndex < numberOfItemsInView - 1 && index != images.length - 1 ? "8px" : "0px";
      }
    },
    [activeIndex, images.length, numberOfItemsInView, size],
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const swipeDistance = touchStartX - touchEndX;
    const swipeThreshold = 50;

    if (Math.abs(touchEndX) > swipeThreshold) {
      if (swipeDistance > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }

    setTouchStartX(0);
    setTouchEndX(0);
  }, [touchStartX, touchEndX, nextSlide, prevSlide]);

  return (
    <CarouselContainer rootStyles={props.rootStyles}>
      <Carousel
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {props.images.map((image, index) => {
          const relativeIndex = (index - activeIndex + images.length) % images.length;
          const getWidthParam = activeIndex > images.length - numberOfItemsInView ? index : relativeIndex;

          return renderCMSComponent(
            {
              ...image,
              relativeIndex: relativeIndex,
              getWidthParam: getWidthParam,
              getWidth: getWidth,
              getMargin: getMargin,
              index,
            },
            undefined,
            index,
          );
        })}
      </Carousel>
      {isDesktop && !isPrevDisabled() && (
        <IconButton
          variant="contained"
          color={"secondary"}
          size={"large"}
          tabIndex={-1}
          onClick={prevSlide}
          sx={arrowIconSx("left")}
        >
          <KeyboardArrowLeftIcon  />
        </IconButton>
      )}
      {isDesktop && !isNextDisabled() && (
        <IconButton
          variant="contained"
          color={"secondary"}
          size={"large"}
          tabIndex={-1}
          onClick={nextSlide}
          sx={arrowIconSx("right")}
        >
          <KeyboardArrowRightIcon  />
        </IconButton>
      )}
    </CarouselContainer>
  );
};

const arrowIconSx = (side: "left" | "right") => ({
  position: "absolute" as const,
  top: "35%",
  boxShadow: "0px 1px 3px 1px #00000026",
  ...(side === "left" ? { left: "10px" } : { right: "10px" }),
});

const CarouselContainer = styled("div", { shouldForwardProp: (prop) => prop !== "rootStyles" })<{
  rootStyles?: string;
}>(({ rootStyles }) => ({
  display: "flex",
  width: "100%",
  position: "relative",
  borderRadius: "28px",
  ...(rootStyles as unknown as Record<string, unknown>),
}));

const Carousel = styled("div")({
  display: "flex",
  width: "100%",
  alignItems: "flex-start",
});
